import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";

interface VideoScrubberProps {
  scrollProgress: number;
}

const VIDEO_SRC = "https://public.readdy.ai/ai/video_res/019e9713-60c3-7ce1-97c9-029c0ba370d5.mp4";

export default function VideoScrubber({ scrollProgress }: VideoScrubberProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetTimeRef = useRef<number>(0);
  const currentTimeRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const scrollRef = useRef<number>(scrollProgress);
  const [isLoaded, setIsLoaded] = useState(false);

  // Keep latest scrollProgress in a ref so the rAF loop stays alive without restarting
  useEffect(() => {
    scrollRef.current = scrollProgress;
  }, [scrollProgress]);

  // Video-ready listener
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => setIsLoaded(true);

    video.addEventListener("canplaythrough", handleCanPlay);
    // Fallback if canplaythrough never fires
    video.addEventListener("loadeddata", handleCanPlay);

    if (video.readyState >= 3) {
      setIsLoaded(true);
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlay);
      video.removeEventListener("loadeddata", handleCanPlay);
    };
  }, []);

  // Scrub loop
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tick = () => {
      const duration = video.duration && !isNaN(video.duration) ? video.duration : 4.2;
      const sp = Math.max(0, Math.min(1, scrollRef.current));
      const target = Math.max(0, Math.min(duration, sp * duration));
      targetTimeRef.current = target;

      currentTimeRef.current += (target - currentTimeRef.current) * 0.15;

      if (
        !video.seeking &&
        Math.abs(video.currentTime - currentTimeRef.current) > 0.01
      ) {
        try {
          video.currentTime = currentTimeRef.current;
        } catch {
          /* ignore seek errors */
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // GSAP mouse parallax
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const mx = e.clientX / window.innerWidth - 0.5;
      const my = e.clientY / window.innerHeight - 0.5;
      gsap.to(container, {
        x: -mx * 40,
        y: -my * 40,
        duration: 1.2,
        ease: "power2.out",
        overwrite: "auto",
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="absolute inset-0 bg-[#0A1A3A] overflow-hidden">
      <div
        ref={containerRef}
        className="absolute inset-0 w-full h-full"
        style={{ transform: "scale(1.05)", willChange: "transform" }}
      >
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          playsInline
          muted
          preload="auto"
          crossOrigin="anonymous"
          className="object-cover w-full h-full pointer-events-none"
          style={{
            imageRendering: "crisp-edges",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
          }}
        />
      </div>

      {!isLoaded && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ backgroundColor: "#0A1A3Af4" }}
        >
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-blue-300/20 animate-ping" />
            <div className="w-10 h-10 rounded-full border-4 border-blue-200/20 border-t-blue-200 animate-spin" />
          </div>
          <div className="mt-6 font-manrope font-semibold text-[12px] uppercase tracking-[0.25em] text-blue-200">
            LOADING SCROLL STREAM...
          </div>
        </div>
      )}
    </div>
  );
}